<template>
  <div class="yhc-service-level-info">
    <div
      v-if="!deliveryAreaNotAvailable"
      class="yhc-service-level-info__content"
    >
      <div
        class="yhc-service-level-info__grid"
        :class="{ 'yhc-service-level-info__grid--user-view': isUserViewActive }"
      >
        <!-- 1列目: お届け可能日（2行分使用）（ユーザー用では非表示） -->
        <div
          v-if="!isUserViewActive"
          class="yhc-service-level-info__column yhc-service-level-info__column--delivery-dates"
        >
          <div class="yhc-service-level-info__section">
            <div
              class="yhc-service-level-info__label yhc-service-level-info__label--delivery-dates"
            >
              <div class="yhc-service-level-info__label-start">
                <Icon
                  name="Calendar"
                  class="yhc-service-level-info__icon"
                />
                <span>{{ deliveryDatesSectionLabel }}</span>
                <span
                  v-if="hasActivePeakSeasonRule && !isCorporationViewActive"
                  class="yhc-service-level-info__peak-season-annotation"
                  @click="togglePeakSeasonTooltip"
                  @click.stop
                >
                  ※ご確認ください (引越シーズン期間の留意点)
                </span>
                <div
                  v-if="
                    showPeakSeasonTooltip &&
                    hasActivePeakSeasonRule &&
                    !isCorporationViewActive
                  "
                  class="yhc-service-level-info__peak-season-tooltip"
                  @click.stop
                >
                  <div class="yhc-service-level-info__peak-season-tooltip-content">
                    <p>
                      お預かりもしくはお届けの日付が
                      <span class="warning"
                        >{{ formatDateForMD(deliveryPeakSeasonRule.startDate) }} ～
                        {{ formatDateForMD(deliveryPeakSeasonRule.endDate) }}</span
                      >(※) となる場合は、引越シーズン期間につき以下の対応となります。
                    </p>
                    <ul class="yhc-service-level-info__peak-season-tooltip-list">
                      <li>
                        <span class="warning">時間帯指定はできません。</span>
                      </li>
                      <li>
                        シーズン加算料金として<span class="warning"
                          >+ ¥{{
                            deliveryPeakSeasonRule.additionalPrice.toLocaleString()
                          }}</span
                        >
                        (税込) /件(※)が発生します。
                      </li>
                      <li v-if="hasDeliverableItemCountsLimitForRule">
                        同一日に同一住所の配送可能数
                        <span class="warning"
                          >{{ deliveryPeakSeasonRule.deliverableItemCounts }}点 </span
                        >までとなります。
                      </li>
                      <li>当日15時までの事前予約で、最短翌々日のお預かりが可能です。</li>
                      <li v-if="deliveryPeakSeasonRule.sameDayDeliveryDisabled">
                        お預かり日当日の配達はご利用いただけません。
                      </li>
                    </ul>
                    <p class="yhc-service-level-info__peak-season-tooltip-notice">
                      ※期間及び加算料金は会社間の契約内容とは異なる場合がございます。
                    </p>
                  </div>
                </div>
              </div>
              <a
                v-if="isCorporationViewActive"
                href="#"
                class="yhc-service-level-info__rank-display-mode-link hover-fade"
                @click.prevent="openRankDisplayModeModal"
              >
                ランク別表示切替え
              </a>
            </div>
            <div class="yhc-service-level-info__value">
              <YhcServiceLevelDeliveryDates
                :is-corporation-view="isCorporationViewActive"
                :rank-display-mode="effectiveRankDisplayMode"
                :lead-time-e-revision-note="leadTimeERevisionNote"
                :lead-time-fg-revision-note="leadTimeFgRevisionNote"
              />
            </div>
          </div>
        </div>

        <!-- 2列目1行目: 指定可能な時間帯 -->
        <div
          class="yhc-service-level-info__column yhc-service-level-info__column--time-band"
        >
          <div class="yhc-service-level-info__section">
            <div class="yhc-service-level-info__label">
              <Icon
                name="Clock"
                class="yhc-service-level-info__icon"
              />
              <span class="mr-2">指定可能な時間帯</span>
              <span
                v-if="!isUserViewActive && !isCorporationViewActive"
                class="yhc-service-level-info__annotation"
              >
                ※会社間の契約料金とは異なる場合がございます。
              </span>
            </div>
            <p
              v-if="
                isUserViewActive && !isCorporationViewActive && timeBandPeakSeasonMessage
              "
              class="yhc-service-level-info__annotation yhc-service-level-info__annotation--danger"
            >
              {{ timeBandPeakSeasonMessage }}
            </p>
            <div class="yhc-service-level-info__value">
              <!-- 時間帯区分カード表示（切り出したコンポーネント） -->
              <YhcServiceLevelTimeBand
                :from-time-band-code="fromTimeBandCode"
                :to-time-band-code="toTimeBandCode"
                :from-just-on-time-option="fromJustOnTimeOption"
                :to-just-on-time-option="toJustOnTimeOption"
                :from-just-on-time-option-notice="fromJustOnTimeOptionNotice"
                :to-just-on-time-option-notice="toJustOnTimeOptionNotice"
                :is-user-view="isUserViewActive"
                :is-corporation-view="isCorporationViewActive"
                :from-revision-note="fromTimeBandRevisionNote"
                :to-revision-note="toTimeBandRevisionNote"
                :from-span-note="fromTimeBandSpan.note"
                :to-span-note="toTimeBandSpan.note"
                :from-span-period-label="fromTimeBandSpan.periodLabel"
                :to-span-period-label="toTimeBandSpan.periodLabel"
                :from-span-tooltip-code="fromTimeBandSpan.tooltipCode"
                :to-span-tooltip-code="toTimeBandSpan.tooltipCode"
              />
            </div>
          </div>
        </div>

        <!-- 3列目1行目: 営業日 -->
        <div
          class="yhc-service-level-info__column yhc-service-level-info__column--working-days"
        >
          <div class="yhc-service-level-info__section">
            <div class="yhc-service-level-info__label">
              <Icon
                name="Calendar"
                class="yhc-service-level-info__icon"
              />
              <span>営業日</span>
            </div>
            <p
              v-if="isUserViewActive"
              class="yhc-service-level-info__annotation"
            >
              ※元々のお預かり日・配達日以降で選んでください
            </p>
            <!-- 営業曜日表示（切り出したコンポーネント） -->
            <YhcServiceLevelDays
              :from-regular-working-days="fromRegularWorkingDays"
              :to-regular-working-days="toRegularWorkingDays"
              :is-fg-rank-available="isFgRankAvailable"
              :rank-display-mode="effectiveRankDisplayMode"
              :is-corporation-view="isCorporationViewActive"
              :from-revision-note="fromWorkingDaysRevisionNote"
              :to-revision-note="toWorkingDaysRevisionNote"
              :from-span-note="fromWorkingDaysSpan.note"
              :to-span-note="toWorkingDaysSpan.note"
              :from-span-period-label="fromWorkingDaysSpan.periodLabel"
              :to-span-period-label="toWorkingDaysSpan.periodLabel"
              :from-span-working-days="fromWorkingDaysSpan.record"
              :to-span-working-days="toWorkingDaysSpan.record"
            />
          </div>
        </div>

        <!-- 2行目: 家財料金（2, 3列の2列分使用）（ユーザー用では非表示） -->
        <div
          v-if="showSizeRankPrices && !isUserViewActive && !isCorporationViewActive"
          class="yhc-service-level-info__column yhc-service-level-info__column--rank-fees"
        >
          <div class="yhc-service-level-info__section">
            <div class="yhc-service-level-info__label">
              <Icon
                name="Yen"
                class="yhc-service-level-info__icon"
                style="font-size: 1.25rem"
              />
              <span>家財料金（サイズランク別）</span>
              <span class="yhc-service-level-info__annotation">
                ※家財おまかせ便の規定料金となります。会社間の契約料金とは異なる場合がございます。
              </span>
              <span
                v-if="showSsSRankFeeNote"
                class="yhc-service-level-info__annotation yhc-service-level-info__annotation--danger"
              >
                {{ ssSRankFeeNote }}
              </span>
              <span class="yhc-service-level-info__annotation">
                ※この箇所は社内用ページでのみ表示されます。
              </span>
            </div>
            <YhcServiceLevelRankFees :prices="sizeRankPriceItems" />
          </div>
        </div>
      </div>
    </div>
    <YhcServiceLevelInfoOffice
      v-if="!deliveryAreaNotAvailable"
      :show="showOfficeSection"
      :from-heading="fromOfficeHeading"
      :to-heading="toOfficeHeading"
      :from-detail-lines="fromOfficeDetailLines"
      :to-detail-lines="toOfficeDetailLines"
      :show-from-ytc-note="fromIsYtcDeliveryArea"
      :show-to-ytc-note="toIsYtcDeliveryArea"
    />
    <div
      v-else
      class="yhc-service-level-info__not-available"
    >
      ※サービス対象外のエリアです
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import Icon from './ui/icons/index.vue'
import { YhcEstimateFormFrontService } from '../../YhcEstimateFormFrontPage/YhcEstimateFormFrontService'
import Badge from './ui/Badge.vue'
import Tooltip from './ui/Tooltip.vue'
import { yhcDeliveryAreaNamesByFlg } from '../../../constants/deliveryArea'
import {
  ModelYhcMastersByZipcode,
  ModelYhcOptions,
  ModelYhcRegularWorkingDays,
  ModelYhcKazaiDeliveryPeakSeasonRules,
} from '../../../fireModels/yhcDataTypes'
import type {
  ModelYhcPrefFromToInfos,
  ModelYhcOffices,
} from '../../../fireModels/yhcDataTypes'
import {
  YhcEFTimeOptionsByTimeBandCode,
  yhcEFTimeOptionsByTimeBandCode,
} from '../../YhcEstimateFormFrontPage/YhcEFUserInputDataHandler'
import YhcServiceLevelTimeBand from './YhcServiceLevelTimeBand.vue'
import YhcServiceLevelDays from './YhcServiceLevelDays.vue'
import YhcServiceLevelDeliveryDates from './YhcServiceLevelDeliveryDates.vue'
import { yhcSizeRanks } from '../../../constants/sizeRanks'
import YhcServiceLevelRankFees, { SizeRankPriceItem } from './YhcServiceLevelRankFees.vue'
import YhcServiceLevelInfoOffice from './YhcServiceLevelInfoOffice.vue'
import { formatDateForMD } from '../../YhcEstimateFormFrontPage/components/YhcEFDatetimeInputs/dateFormatter'
import {
  hasDeliverableItemCountsLimit,
  hasPeakSeasonRule,
} from '../../YhcEstimateFormFrontPage/components/peakSeasonRuleUtils'
import YhcServiceLevelRankDisplayModeModal from './YhcServiceLevelRankDisplayModeModal.vue'
import {
  loadCorporationRankDisplayMode,
  saveCorporationRankDisplayMode,
  resolveEffectiveRankDisplayMode,
  type CorporationRankDisplayMode,
} from '../utils/yhcServiceLevelRankDisplayMode'
import {
  buildLeadTimeRevisionNotes,
  buildRevisionNotesForMunicipality,
  municipalityFromZipMaster,
  type ServiceLevelRevisionRecord,
} from '../serviceLevelRevisionUtils'
import {
  buildMasterVersionSpanNotesFromDiff,
  combineRevisionNotes,
  emptyMasterVersionSpanDiff,
  emptyMasterVersionSpanNotes,
  fetchAndCompareMasterVersionSpan,
  formatMasterVersionSpanNote,
  formatMasterVersionSpanPeriodLabel,
  resolveMasterVersionSpanRange,
  type MasterVersionSpanDiff,
  type MasterVersionSpanNotes,
} from '../masterVersionSpanNotes'
import { pickCoveringWorkingDayRecord } from '../../YhcEstimateFormFrontPage/workingDaysByDate'
import {
  listPickupDatesDeliveringBy,
  shouldShowSsSRankFeeNote,
  SS_S_RANK_FEE_NOTE,
} from '../ssSRankFeeNote'

export default defineComponent({
  name: 'YhcServiceLevelInfo',
  components: {
    Icon,
    Badge,
    Tooltip,
    YhcServiceLevelTimeBand,
    YhcServiceLevelDays,
    YhcServiceLevelDeliveryDates,
    YhcServiceLevelRankFees,
    YhcServiceLevelInfoOffice,
  },
  setup() {
    return {
      yhcDeliveryAreaNamesByFlg,
      formatDateForMD,
      ssSRankFeeNote: SS_S_RANK_FEE_NOTE,
    }
  },
  data() {
    return {
      showPeakSeasonTooltip: false,
      rankDisplayMode: loadCorporationRankDisplayMode() as CorporationRankDisplayMode,
      masterVersionSpanDiff: null as MasterVersionSpanDiff | null,
      masterVersionSpanFetchGeneration: 0,
    }
  },
  watch: {
    masterVersionSpanFetchKey: {
      handler() {
        void this.refreshMasterVersionSpanDiff()
      },
      immediate: true,
    },
    effectiveRankDisplayMode: {
      handler(mode: CorporationRankDisplayMode) {
        const u = this.$yhcefs?.$userInputData
        if (!u) return
        if (u.serviceLevelRankDisplayMode === mode) return
        u.serviceLevelRankDisplayMode = mode
        void u.refreshMastersForSelectedDates()
      },
      immediate: true,
    },
  },
  mounted() {
    // 外部クリックでツールチップを閉じる
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  inject: {
    YhcEstimateFormFrontServiceInstance: {
      from: 'YhcEstimateFormFrontServiceInstance',
    },
    serviceLevelRevisions: {
      from: 'serviceLevelRevisions',
      default: () => [],
    },
    serviceLevelRevisionNotesEnabled: {
      from: 'serviceLevelRevisionNotesEnabled',
      default: false,
    },
    masterVersionSpanNotesEnabled: {
      from: 'masterVersionSpanNotesEnabled',
      default: false,
    },
  },
  computed: {
    $yhcefs(): YhcEstimateFormFrontService {
      return this.YhcEstimateFormFrontServiceInstance as YhcEstimateFormFrontService
    },
    revisionAsOfDate(): string {
      const u = this.$yhcefs.$userInputData
      if (!u) return $core.$dayjs().format('YYYY-MM-DD')
      if (u.serviceLevelDateMode === 'delivery') {
        return u.deliverySelectedDate || $core.$dayjs().format('YYYY-MM-DD')
      }
      return u.pickupSelectedDate || $core.$dayjs().format('YYYY-MM-DD')
    },
    masterVersionSpanFetchKey(): string {
      if (!this.masterVersionSpanNotesEnabled) return 'off'
      const u = this.$yhcefs?.$userInputData
      if (!u) return 'off'
      const periods = (this.$yhcefs.masterVersionPeriods || [])
        .map((p) => `${p.id}:${(p.enabledFrom || '').slice(0, 10)}`)
        .join(',')
      return [
        u.serviceLevelDateMode,
        u.pickupSelectedDate || '',
        String(!!u.pickupDateResetFlag),
        u.deliverySelectedDate || '',
        String(!!u.deliveryDateResetFlag),
        u.zip_from || '',
        u.zip_to || '',
        this.$yhcefs.versionPickup || '',
        this.$yhcefs.versionDelivery || '',
        periods,
      ].join('|')
    },
    masterVersionSpanNotes() {
      if (!this.masterVersionSpanNotesEnabled || !this.masterVersionSpanDiff) {
        return emptyMasterVersionSpanNotes()
      }
      return buildMasterVersionSpanNotesFromDiff(this.masterVersionSpanDiff)
    },
    fromMunicipality() {
      return municipalityFromZipMaster(this.fromZipMaster)
    },
    toMunicipality() {
      return municipalityFromZipMaster(this.toZipMaster)
    },
    fromWorkingDaysRevisionNote(): string {
      if (!this.serviceLevelRevisionNotesEnabled) return ''
      return (
        buildRevisionNotesForMunicipality(
          this.serviceLevelRevisions as ServiceLevelRevisionRecord[],
          this.fromMunicipality,
          this.revisionAsOfDate,
        ).workingDays || ''
      )
    },
    toWorkingDaysRevisionNote(): string {
      if (!this.serviceLevelRevisionNotesEnabled) return ''
      return (
        buildRevisionNotesForMunicipality(
          this.serviceLevelRevisions as ServiceLevelRevisionRecord[],
          this.toMunicipality,
          this.revisionAsOfDate,
        ).workingDays || ''
      )
    },
    fromTimeBandRevisionNote(): string {
      if (!this.serviceLevelRevisionNotesEnabled) return ''
      return (
        buildRevisionNotesForMunicipality(
          this.serviceLevelRevisions as ServiceLevelRevisionRecord[],
          this.fromMunicipality,
          this.revisionAsOfDate,
        ).timeBand || ''
      )
    },
    toTimeBandRevisionNote(): string {
      if (!this.serviceLevelRevisionNotesEnabled) return ''
      return (
        buildRevisionNotesForMunicipality(
          this.serviceLevelRevisions as ServiceLevelRevisionRecord[],
          this.toMunicipality,
          this.revisionAsOfDate,
        ).timeBand || ''
      )
    },
    fromTimeBandSpan(): { note: string; periodLabel: string; tooltipCode: string } {
      return this.timeBandSpanUi('pickup')
    },
    toTimeBandSpan(): { note: string; periodLabel: string; tooltipCode: string } {
      return this.timeBandSpanUi('delivery')
    },
    pickupWorkingDaysOverrideRecord(): ModelYhcRegularWorkingDays | null {
      const notes = this.masterVersionSpanNotes
      if (notes.uncertainSide !== 'pickup' || notes.workingDaysPeriod !== 'before') {
        return null
      }
      return this.workingDaysRecord(
        notes.workingDaysDisplayOverridePattern,
        notes.workingDaysDisplayOverrideAsOf,
      )
    },
    fromWorkingDaysSpan(): {
      note: string
      periodLabel: string
      record: ModelYhcRegularWorkingDays | null
    } {
      return this.workingDaysSpanUi('pickup')
    },
    toWorkingDaysSpan(): {
      note: string
      periodLabel: string
      record: ModelYhcRegularWorkingDays | null
    } {
      return this.workingDaysSpanUi('delivery')
    },
    leadTimeRevisionNotes() {
      if (!this.serviceLevelRevisionNotesEnabled) {
        return { leadTimeE: null, leadTimeFg: null }
      }
      return buildLeadTimeRevisionNotes(
        this.serviceLevelRevisions as ServiceLevelRevisionRecord[],
        [this.fromMunicipality, this.toMunicipality],
        this.revisionAsOfDate,
      )
    },
    leadTimeERevisionNote(): string {
      if (!this.showERankForLeadTimeNote) return ''
      return combineRevisionNotes(
        this.leadTimeRevisionNotes.leadTimeE,
        this.masterVersionSpanNotes.leadTimeENote,
      )
    },
    leadTimeFgRevisionNote(): string {
      if (!this.showFgRankForLeadTimeNote) return ''
      return combineRevisionNotes(
        this.leadTimeRevisionNotes.leadTimeFg,
        this.masterVersionSpanNotes.leadTimeFgNote,
      )
    },
    showERankForLeadTimeNote(): boolean {
      const m = this.effectiveRankDisplayMode
      return m === 'both' || m === 'e_only'
    },
    showFgRankForLeadTimeNote(): boolean {
      const m = this.effectiveRankDisplayMode
      return m === 'both' || m === 'fg_only'
    },
    /** 発着＝お届け日のとき「お預かり可能日」、それ以外は「お届け可能日」 */
    deliveryDatesSectionLabel(): string {
      const mode = this.$yhcefs.$userInputData.serviceLevelDateMode
      return mode === 'delivery' ? 'お預かり可能日' : 'お届け可能日'
    },
    effectiveRankDisplayMode(): CorporationRankDisplayMode {
      return resolveEffectiveRankDisplayMode(
        this.rankDisplayMode,
        this.isCorporationViewActive,
      )
    },
    fromZipMaster(): ModelYhcMastersByZipcode {
      return this.$yhcefs.$userInputData.zip_from_yhc_master
    },
    toZipMaster(): ModelYhcMastersByZipcode {
      return this.$yhcefs.$userInputData.zip_to_yhc_master
    },
    /** 配達エリアフラグ（API が数値でも文字列でも比較できるよう正規化） */
    fromDeliveryAreaFlg(): string {
      const raw = this.fromZipMaster?.deliveryAreaFlag
      return raw === undefined || raw === null ? '' : String(raw)
    },
    toDeliveryAreaFlg(): string {
      const raw = this.toZipMaster?.deliveryAreaFlag
      return raw === undefined || raw === null ? '' : String(raw)
    },
    /** 郵便番号マスタの配達エリアフラグが 0（YTCエリア） */
    fromIsYtcDeliveryArea(): boolean {
      return this.fromDeliveryAreaFlg === '0'
    },
    toIsYtcDeliveryArea(): boolean {
      return this.toDeliveryAreaFlg === '0'
    },
    // 対応不可エリアかどうか
    deliveryAreaNotAvailable(): boolean {
      // 親コンポーネントからisFleaMktViewActiveを取得
      const isFleaMkt = this.$parent?.isFleaMktViewActive

      if (isFleaMkt) {
        // フリマ用ページの場合は0,2,3すべてをチェック
        return (
          this.fromDeliveryAreaFlg === '0' ||
          this.fromDeliveryAreaFlg === '2' ||
          this.fromDeliveryAreaFlg === '3' ||
          this.toDeliveryAreaFlg === '0' ||
          this.toDeliveryAreaFlg === '2' ||
          this.toDeliveryAreaFlg === '3'
        )
      } else {
        // 通常の場合は従来通り3のみチェック
        return this.fromDeliveryAreaFlg === '3' || this.toDeliveryAreaFlg === '3'
      }
    },
    // フリマ用ページかどうかの判定
    isFleaMktView(): boolean {
      return !!this.$parent?.isFleaMktViewActive
    },
    // ユーザー用ページかどうかの判定
    isUserViewActive(): boolean {
      return !!(this.$parent as { isUserViewActive?: boolean })?.isUserViewActive
    },
    // 法人向けページかどうかの判定
    isCorporationViewActive(): boolean {
      return !!(this.$parent as { isCorporationViewActive?: boolean })
        ?.isCorporationViewActive
    },
    fromDeliveryArea(): string {
      return yhcDeliveryAreaNamesByFlg(this.fromDeliveryAreaFlg)
    },
    toDeliveryArea(): string {
      return yhcDeliveryAreaNamesByFlg(this.toDeliveryAreaFlg)
    },
    // 発地の時間帯区分コード
    fromTimeBandCode(): string {
      const override = this.masterVersionSpanNotes.timeBandDisplayOverrideCode
      if (this.masterVersionSpanNotes.uncertainSide === 'pickup' && override) {
        return override
      }
      return this.fromZipMaster?.timeBand || '3'
    },
    // 着地の時間帯区分コード
    toTimeBandCode(): string {
      return this.toZipMaster?.timeBand || '3'
    },
    fromTimeOptionByTimeBandCode(): YhcEFTimeOptionsByTimeBandCode {
      return yhcEFTimeOptionsByTimeBandCode[this.fromTimeBandCode]
    },
    toTimeOptionByTimeBandCode(): YhcEFTimeOptionsByTimeBandCode {
      return yhcEFTimeOptionsByTimeBandCode[this.toTimeBandCode]
    },
    // 発地のジャストオンタイムオプション
    fromJustOnTimeOption(): ModelYhcOptions {
      return this.$yhcefs.generalOptionsByKey[
        this.fromTimeOptionByTimeBandCode?.justInTimeOption?.price
      ]
    },
    // 着地のジャストオンタイムオプション
    toJustOnTimeOption(): ModelYhcOptions {
      return this.$yhcefs.generalOptionsByKey[
        this.toTimeOptionByTimeBandCode?.justInTimeOption?.price
      ]
    },
    // 発地のジャストオンタイムのnoticeからhtml表記を削除して返す
    fromJustOnTimeOptionNotice(): string {
      return this.fromJustOnTimeOption?.notice?.replace(/<[^>]*>?/g, '')
    },
    // 着地のジャストオンタイムのnoticeからhtml表記を削除して返す
    toJustOnTimeOptionNotice(): string {
      return this.toJustOnTimeOption?.notice?.replace(/<[^>]*>?/g, '')
    },
    fromOffice(): string {
      if (this.fromDeliveryAreaFlg === '2') {
        return 'ヤマト運輸事業所'
      }
      return this.$yhcefs.$userInputData.from_office_master?.name || ''
    },
    toOffice(): string {
      if (this.toDeliveryAreaFlg === '2') {
        return 'ヤマト運輸事業所'
      }
      return this.$yhcefs.$userInputData.to_office_master?.name || ''
    },

    showOfficeSection(): boolean {
      return !!(
        this.fromZipMaster &&
        this.toZipMaster &&
        !this.isFleaMktView &&
        !this.isUserViewActive &&
        !this.isCorporationViewActive
      )
    },
    fromOfficeHeading(): string {
      const base =
        typeof this.fromOffice === 'string' && this.fromOffice.trim()
          ? this.fromOffice.trim()
          : '担当事業所'
      const code =
        (this.$yhcefs.$userInputData.from_office_master?.code ?? '').trim() ||
        (this.fromZipMaster?.kazaiStoreCode ?? '').trim()
      return code ? `${base}（${code}）` : base
    },
    toOfficeHeading(): string {
      const base =
        typeof this.toOffice === 'string' && this.toOffice.trim()
          ? this.toOffice.trim()
          : '担当事業所'
      const code =
        (this.$yhcefs.$userInputData.to_office_master?.code ?? '').trim() ||
        (this.toZipMaster?.kazaiStoreCode ?? '').trim()
      return code ? `${base}（${code}）` : base
    },
    /** 配達エリア 2（ヤマト運輸事業所固定表記）のときはマスタの住所・電話を出さない */
    shouldShowFromOfficeDetails(): boolean {
      const flg = this.fromDeliveryAreaFlg
      if (flg === '2') return false
      return !!this.$yhcefs.$userInputData.from_office_master
    },
    shouldShowToOfficeDetails(): boolean {
      const flg = this.toDeliveryAreaFlg
      if (flg === '2') return false
      return !!this.$yhcefs.$userInputData.to_office_master
    },
    fromOfficeDetailLines(): string[] {
      if (!this.shouldShowFromOfficeDetails) return []
      return this.buildOfficeDetailLines(this.$yhcefs.$userInputData.from_office_master)
    },
    toOfficeDetailLines(): string[] {
      if (!this.shouldShowToOfficeDetails) return []
      return this.buildOfficeDetailLines(this.$yhcefs.$userInputData.to_office_master)
    },

    // お預かり先・お届け先の営業日パターンを取得
    fromRegularWorkingDays(): ModelYhcRegularWorkingDays | null {
      return (
        this.pickupWorkingDaysOverrideRecord ||
        this.$yhcefs.$userInputData.from_regular_working_days_master
      )
    },

    toRegularWorkingDays(): ModelYhcRegularWorkingDays | null {
      return this.$yhcefs.$userInputData.to_regular_working_days_master
    },

    isFgRankAvailable(): boolean {
      return this.$yhcefs.$userInputData.isFgRankAvailable
    },
    prefFromToInfo(): ModelYhcPrefFromToInfos | null {
      return this.$yhcefs.$userInputData.pref_from_to_info_master
    },
    sizeRankPriceItems(): SizeRankPriceItem[] {
      if (this.isFleaMktView || !this.prefFromToInfo) {
        return []
      }

      const priceKeyMap: {
        key: keyof typeof yhcSizeRanks
        field: keyof ModelYhcPrefFromToInfos
        sizeRange: string
      }[] = [
        { key: 'ss', field: 'price_ss', sizeRange: '80cmまで' },
        { key: 's', field: 'price_s', sizeRange: '120cmまで' },
        { key: 'a', field: 'price_a', sizeRange: '160cmまで' },
        { key: 'b', field: 'price_b', sizeRange: '200cmまで' },
        { key: 'c', field: 'price_c', sizeRange: '250cmまで' },
        { key: 'd', field: 'price_d', sizeRange: '300cmまで' },
        { key: 'e', field: 'price_e', sizeRange: '350cmまで' },
        { key: 'f', field: 'price_f', sizeRange: '400cmまで' },
        { key: 'g', field: 'price_g', sizeRange: '450cmまで' },
      ]

      return priceKeyMap
        .map(({ key, field, sizeRange }) => {
          const rawPrice = this.prefFromToInfo?.[field]
          if (typeof rawPrice !== 'number') {
            return null
          }
          const priceNotaxField = `${field}_notax` as keyof ModelYhcPrefFromToInfos
          const rawPriceNotax = this.prefFromToInfo?.[priceNotaxField]
          const priceNotax = typeof rawPriceNotax === 'number' ? rawPriceNotax : 0
          return {
            rank: yhcSizeRanks[key].label,
            sizeLabel: sizeRange,
            price: rawPrice,
            priceText: `${rawPrice.toLocaleString('ja-JP')}円`,
            priceNotax: priceNotax,
            priceNotaxText: `${priceNotax.toLocaleString('ja-JP')}円`,
          }
        })
        .filter((row): row is SizeRankPriceItem => row !== null)
    },
    showSizeRankPrices(): boolean {
      return this.sizeRankPriceItems.length > 0
    },
    /** 2027-01-01 以降の SS/S 単独依頼を A ランク料金にする注釈 */
    showSsSRankFeeNote(): boolean {
      const u = this.$yhcefs?.$userInputData
      if (!u) return false
      const sameDayDeliveryAvailable =
        !this.$yhcefs.isFleaMarket &&
        u.sameDayPickupAndDeliveryAvailable &&
        !u.deliveryPeakSeasonRule?.sameDayDeliveryDisabled
      return shouldShowSsSRankFeeNote({
        dateMode: u.serviceLevelDateMode,
        pickupSelectedDate: u.pickupSelectedDate,
        pickupDateResetFlag: u.pickupDateResetFlag,
        deliverySelectedDate: u.deliverySelectedDate,
        deliveryDateResetFlag: u.deliveryDateResetFlag,
        availablePickupDates: this.pickupDatesForSsSRankFeeNote,
        sameDayDeliveryAvailable,
      })
    },
    /**
     * お届け日指定で画面に出しているお預かり可能日。
     * お預かり日指定、またはお届け日が無効なときは空。
     */
    pickupDatesForSsSRankFeeNote(): string[] {
      const u = this.$yhcefs?.$userInputData
      if (!u || u.serviceLevelDateMode !== 'delivery') return []
      if (
        !u.deliverySelectedDate ||
        u.deliveryDateResetFlag ||
        !u.pref_from_to_info_master
      ) {
        return []
      }
      const listFor = (sizeRank: 'sizeRank0NeedDays' | 'sizeRank1NeedDays') =>
        listPickupDatesDeliveringBy({
          deliveryDate: u.deliverySelectedDate as string,
          isWorkingDay: (ymd) => u.isPickupWorkingDayForDate(ymd),
          deliveryDateForPickup: (ymd) =>
            u.computeDeliveryDateForPickupDate(ymd, sizeRank),
        })
      const dates = listFor('sizeRank0NeedDays')
      if (!u.isFgRankAvailable) return dates
      return dates.concat(listFor('sizeRank1NeedDays'))
    },
    // Eランク以下の最短お届け日
    earliestDeliveryDate(): string {
      return this.$yhcefs.$userInputData.deliveryDate
    },
    // FGランクの最短お届け日
    fgEarliestDeliveryDate(): string {
      return this.$yhcefs.$userInputData.fgDeliveryDate
    },
    // 離島かどうか
    isRemoteIsland(): boolean {
      return (
        this.$yhcefs.$userInputData.zip_from_yhc_master?.isRemoteIsland === true ||
        this.$yhcefs.$userInputData.zip_to_yhc_master?.isRemoteIsland === true
      )
    },
    // 当日自発着かどうか
    isSameDayDelivery(): boolean {
      return this.$yhcefs.$userInputData.isSameDayPickupAndDelivery
    },
    // Eランク以下の全てのお届け可能日（繁忙期チェック用）
    allDeliveryDatesForPeakSeasonCheck(): string[] {
      if (
        !this.$yhcefs.$userInputData.pref_from_to_info_master ||
        this.$yhcefs.$userInputData.deliveryDateDisplayErrorMessage ||
        this.isSameDayDelivery ||
        this.$yhcefs.$userInputData.pickupDateResetFlag
      ) {
        return this.earliestDeliveryDate ? [this.earliestDeliveryDate] : []
      }

      const pickupDate = this.$yhcefs.$userInputData.pickupSelectedDate
      if (!pickupDate) return []

      if (this.isRemoteIsland) {
        const adjustedDate = this.$yhcefs.$userInputData.remoteIslandDeliveryDateAdjusted
        if (adjustedDate) {
          return [adjustedDate]
        }
        const remoteIslandDate = $core
          .$dayjs(pickupDate)
          .add(14, 'day')
          .format('YYYY-MM-DD')
        return [remoteIslandDate]
      }

      const endDate = $core.$dayjs(pickupDate).add(14, 'day').format('YYYY-MM-DD')

      return this.calculateDeliveryDatesInPeriod(
        this.earliestDeliveryDate,
        endDate,
      )
    },
    // FGランクの全てのお届け可能日（繁忙期チェック用）
    fgDeliveryDatesForPeakSeasonCheck(): string[] {
      if (
        !this.$yhcefs.$userInputData.pref_from_to_info_master ||
        this.$yhcefs.$userInputData.deliveryDateDisplayErrorMessage ||
        this.isSameDayDelivery ||
        this.$yhcefs.$userInputData.pickupDateResetFlag
      ) {
        return this.fgEarliestDeliveryDate ? [this.fgEarliestDeliveryDate] : []
      }

      const pickupDate = this.$yhcefs.$userInputData.pickupSelectedDate
      if (!pickupDate) return []

      if (this.isRemoteIsland) {
        const adjustedDate = this.$yhcefs.$userInputData.remoteIslandDeliveryDateAdjusted
        if (adjustedDate) {
          return [adjustedDate]
        }
        const remoteIslandDate = $core
          .$dayjs(pickupDate)
          .add(14, 'day')
          .format('YYYY-MM-DD')
        return [remoteIslandDate]
      }

      const endDate = $core.$dayjs(pickupDate).add(14, 'day').format('YYYY-MM-DD')

      return this.calculateDeliveryDatesInPeriod(
        this.fgEarliestDeliveryDate,
        endDate,
      )
    },
    // 繁忙期シーズンルールマスタ（サービスレベル検索専用）
    deliveryPeakSeasonRule(): ModelYhcKazaiDeliveryPeakSeasonRules | undefined {
      const mode = this.$yhcefs.$userInputData.serviceLevelDateMode
      const rules = this.$yhcefs.deliveryPeakSeasonRules || []

      // お届け日指定モード：表示されるお預かり可能日〜お届け日の範囲が繁忙期にかかるか
      if (mode === 'delivery') {
        const D = this.$yhcefs.$userInputData.deliverySelectedDate
        if (!D) return undefined
        const rangeStart = $core.$dayjs(D).subtract(14, 'day').format('YYYY-MM-DD')
        return rules.find((rule) => rangeStart <= rule.endDate && D >= rule.startDate)
      }

      // お預かり日指定モード：お預かり日とお届け可能日候補のいずれかが繁忙期にかかるか
      const pickupDate = this.$yhcefs.$userInputData.pickupSelectedDate
      if (!pickupDate) return undefined

      const allDates = [
        pickupDate,
        ...this.allDeliveryDatesForPeakSeasonCheck,
        ...this.fgDeliveryDatesForPeakSeasonCheck,
      ]

      return rules.find((rule) => {
        return allDates.some((date) => {
          return (
            (date >= rule.startDate && date <= rule.endDate) ||
            (pickupDate <= rule.startDate && date >= rule.endDate)
          )
        })
      })
    },
    hasActivePeakSeasonRule(): boolean {
      return hasPeakSeasonRule(this.deliveryPeakSeasonRule)
    },
    hasDeliverableItemCountsLimitForRule(): boolean {
      return hasDeliverableItemCountsLimit(this.deliveryPeakSeasonRule)
    },
    /**
     * 指定可能な時間帯ブロック用：繁忙期「開始2ヶ月前～終了日」の表示期間内なら
     * 「※X/Y～Z/Wは選べません」の文言を返す（ユーザー用ページで表示）
     */
    timeBandPeakSeasonMessage(): string | null {
      const rules = this.$yhcefs.deliveryPeakSeasonRules || []
      const today = $core.$dayjs().format('YYYY-MM-DD')

      for (const rule of rules) {
        const start = $core.$dayjs(rule.startDate)
        const end = $core.$dayjs(rule.endDate)
        if (!start.isValid() || !end.isValid()) continue

        const periodStart = start.subtract(2, 'month').format('YYYY-MM-DD')
        const periodEnd = end.format('YYYY-MM-DD')
        if (today >= periodStart && today <= periodEnd) {
          return `※${this.formatDateForMD(rule.startDate)}～${this.formatDateForMD(rule.endDate)}は引越シーズンにつき時間帯を指定できません。`
        }
      }
      return null
    },
  },
  methods: {
    timeBandSpanUi(side: 'pickup' | 'delivery'): {
      note: string
      periodLabel: string
      tooltipCode: string
    } {
      const notes = this.masterVersionSpanNotes
      if (notes.uncertainSide !== side || !notes.timeBandNote) {
        return { note: '', periodLabel: '', tooltipCode: '' }
      }
      return {
        note: notes.timeBandNote,
        periodLabel: notes.timeBandPeriodLabel || '',
        tooltipCode: notes.timeBandTooltipCode ?? '',
      }
    },
    workingDaysRecord(
      pattern: string | null | undefined,
      asOf: string | null | undefined,
    ): ModelYhcRegularWorkingDays | null {
      if (!pattern || !asOf) return null
      return pickCoveringWorkingDayRecord(
        this.$yhcefs.$userInputData.regularWorkingDayRecords,
        pattern,
        asOf,
      ) as ModelYhcRegularWorkingDays | null
    },
    workingDaysSpanUi(side: 'pickup' | 'delivery'): {
      note: string
      periodLabel: string
      record: ModelYhcRegularWorkingDays | null
    } {
      const notes: MasterVersionSpanNotes = this.masterVersionSpanNotes
      if (notes.uncertainSide !== side || !notes.workingDaysNote) {
        return { note: '', periodLabel: '', record: null }
      }
      const downgrade =
        side === 'pickup' &&
        notes.workingDaysPeriod === 'before' &&
        !this.pickupWorkingDaysOverrideRecord
      if (downgrade) {
        return {
          note:
            formatMasterVersionSpanNote(
              notes.workingDaysRevisionDate || '',
              'workingDays',
              'pickup',
              'after',
            ) || '',
          periodLabel:
            formatMasterVersionSpanPeriodLabel(
              notes.workingDaysRevisionDate || '',
              'after',
            ) || '',
          record: this.workingDaysRecord(
            notes.workingDaysOtherPattern,
            notes.workingDaysRevisionDate,
          ),
        }
      }
      return {
        note: notes.workingDaysNote,
        periodLabel: notes.workingDaysPeriodLabel || '',
        record: this.workingDaysRecord(
          notes.workingDaysTooltipPattern,
          notes.workingDaysTooltipAsOf,
        ),
      }
    },
    async refreshMasterVersionSpanDiff() {
      const generation = ++this.masterVersionSpanFetchGeneration
      this.masterVersionSpanDiff = null
      if (!this.masterVersionSpanNotesEnabled) return
      const u = this.$yhcefs?.$userInputData
      if (!u) return
      const range = resolveMasterVersionSpanRange(
        u.serviceLevelDateMode,
        u.pickupSelectedDate,
        u.pickupDateResetFlag,
        u.deliverySelectedDate,
        u.deliveryDateResetFlag,
      )
      if (!range) return
      try {
        const diff = await fetchAndCompareMasterVersionSpan({
          versions: this.$yhcefs.masterVersionPeriods || [],
          rangeStart: range.start,
          rangeEnd: range.end,
          uncertainSide: range.uncertainSide,
          zipFrom: u.zip_from || '',
          zipTo: u.zip_to || '',
          versionPickup: this.$yhcefs.versionPickup || '',
          versionDelivery: this.$yhcefs.versionDelivery || '',
        })
        if (generation !== this.masterVersionSpanFetchGeneration) return
        this.masterVersionSpanDiff = diff
      } catch (error) {
        console.error('マスタバージョンまたぎ注釈の取得に失敗しました:', error)
        if (generation !== this.masterVersionSpanFetchGeneration) return
        this.masterVersionSpanDiff = emptyMasterVersionSpanDiff(range.uncertainSide)
      }
    },
    buildOfficeDetailLines(office: ModelYhcOffices | null): string[] {
      if (!office) return []
      const lines: string[] = []
      const zipRaw = (office.zip ?? '').trim()
      if (zipRaw) {
        lines.push(
          zipRaw.length === 7
            ? `〒${zipRaw.slice(0, 3)}-${zipRaw.slice(3)}`
            : `〒${zipRaw}`,
        )
      }
      const addr = [office.prefecture, office.address1, office.address2]
        .map((s) => (s ?? '').trim())
        .filter(Boolean)
        .join(' ')
      if (addr) lines.push(addr)
      const t2 = (office.tel2 ?? '').trim()
      if (t2) lines.push(t2)
      return lines
    },
    togglePeakSeasonTooltip() {
      this.showPeakSeasonTooltip = !this.showPeakSeasonTooltip
    },
    openRankDisplayModeModal() {
      $core.$modals.openModal({
        modalId: 'yhcServiceLevelRankDisplayMode',
        component: YhcServiceLevelRankDisplayModeModal,
        componentProps: {
          modelValue: this.rankDisplayMode,
          onModeSelected: (mode: CorporationRankDisplayMode) => {
            this.rankDisplayMode = mode
            saveCorporationRankDisplayMode(mode)
          },
        },
        modalProps: {
          title: 'ランク別表示切替え',
          size: 'sm',
          centered: true,
          okOnly: true,
          okTitle: '閉じる',
        },
      })
    },
    handleOutsideClick(event: MouseEvent) {
      const target = event.target as HTMLElement
      if (
        !target.closest('.yhc-service-level-info__peak-season-annotation') &&
        !target.closest('.yhc-service-level-info__peak-season-tooltip')
      ) {
        this.showPeakSeasonTooltip = false
      }
    },
    // 期間内のお届け可能日を計算
    calculateDeliveryDatesInPeriod(
      startDateStr: string,
      endDateStr: string,
    ): string[] {
      const results: string[] = []
      if (!startDateStr || !endDateStr) return results

      let currentDate = $core.$dayjs(startDateStr)
      const endDate = $core.$dayjs(endDateStr)

      if (currentDate.isAfter(endDate)) return results

      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
        const dateStr = currentDate.format('YYYY-MM-DD')
        if (this.$yhcefs.$userInputData.isDeliveryWorkingDayForDate(dateStr)) {
          results.push(dateStr)
        }
        currentDate = currentDate.add(1, 'day')
      }
      return results
    },
  },
})
</script>
